/* eslint-disable @typescript-eslint/no-use-before-define */
import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { BottomAction } from '@euler/app/components/BottomAction';
import { LinkButton } from '@euler/app/components/LinkButton';
import { Typography } from '@euler/app/components/typography';
import { useVoiceFeatures } from '@euler/app/components/voice/useVoiceFeatures';
import { PredefinedAnnotationTags } from '@euler/app/flows/media/annotation/functions/tagPresets';
import { useMediaCaptureWithAnnotation } from '@euler/app/flows/media/functions/useMediaCaptureWithAnnotation';
import { TaskImageWatermark } from '@euler/app/flows/task/components/ImageWatermark';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { MediaInfo } from '@euler/app/flows/task/functions/models';
import { CustomIssueInspectionManager } from '@euler/app/flows/task/functions/task-detail/inspection/CustomIssueInspectionManager';
import { mediaStagedToMediaInfo } from '@euler/app/flows/task/functions/task-detail/util';
import {
  CustomIssueInfluenceFactor,
  CustomIssueInfluenceFactors,
  matchCustomIssueInfluenceFactor,
} from '@euler/app/flows/task/pages/inspection/functions/customIssueInfluence';
import { RelatedFaultLikeSubjectsToSolve } from '@euler/app/flows/task/pages/inspection/site-inspection/components/ItemInspectionView/RelatedFaultLikeSubjectsToSolve';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import {
  ItemProps,
  SectionProps,
  TableView,
} from '@euler/components/TableView';
import { Input } from '@euler/components/form/Input';
import { AppHoldItem, AppMenu } from '@euler/components/holdmenu/AppHoldMenu';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import {
  defectiveLevelDistributed,
  getDefectiveLevel,
  getDefectiveLevelBgColor,
  getDefectiveLevelColor,
  getDefectiveLevelText,
} from '@euler/functions/getDefectiveLevel';
import { isFaultLikeServiceSubject } from '@euler/functions/isFaultLikeServiceSubject';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useFormController } from '@euler/lib/form/controller';
import { useToggleAutomaticKeyboard } from '@euler/lib/keyboard';
import { DefectiveLevel } from '@euler/model';
import {
  ImageAnnotationScene,
  InspectionCategory,
  SiteInspectionType,
} from '@euler/model/enum';
import {
  CustomIssueItemStaged,
  Inspection,
  TaskMediaStaged,
} from '@euler/model/task-detail';
import { RelatedFaultLikeSubject } from '@euler/model/task-detail/RelatedFaultLikeSubject';
import { delayed, showAlert, withConfirmation } from '@euler/utils';
import {
  useBehaviorSubject,
  useBehaviorSubjectUpdater,
  useCreation,
  usePersistFn,
} from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useTheme } from 'styled-components';
import { ItemMediaList } from '../../../components/ItemMediaList';
import { useSiteInspectionNavigatorContext } from './SiteInspectionContext';
import {
  SiteInspectionNavParams,
  usePresentSiteInspectionNavigateAway,
} from './SiteInspectionNavigator';
import { ScrollViewContainer } from './components/ScrollViewContainer';

type CustomIssueForm = {
  siteName?: string;
  inspectionResult: string;
  maintenanceAdvice: string;
  defectiveLevel?: DefectiveLevel;
  description?: string;
  medias?: TaskMediaStaged[];
  influenceFactor?: CustomIssueInfluenceFactor;
  relatedFaultLikeSubjects?: RelatedFaultLikeSubject[];
};

const DefectiveLevels: DefectiveLevel[] = [
  DefectiveLevel.Urgent,
  DefectiveLevel.Warning,
  DefectiveLevel.Defective,
];

export const CustomIssueScreen = wrapNavigatorScreen(
  ({
    siteId,
    customIssue,
    showMedias,
    category,
    inspectionType,
    flowId,
    onDone,
    onDelete,
  }: {
    siteId?: number;
    title?: string;
    flowId: string;
    category: InspectionCategory;
    inspectionType: SiteInspectionType;
    customIssue?: CustomIssueItemStaged;
    showMedias?: boolean;
    onDone?: (manager: CustomIssueInspectionManager<Inspection>) => void;
    onDelete?: (manager: CustomIssueInspectionManager<Inspection>) => void;
  }) => {
    useToggleAutomaticKeyboard(true);

    const theme = useTheme();
    const styles = useStyles();
    const { rpx } = useResponsiveContext();
    const { isVoiceInputEnabled } = useVoiceFeatures();

    const {
      inspection: {
        customIssue: { placeholderColor, borderColor },
      },
    } = theme;

    const {
      taskManager,
      inventoryQuery: { getSiteById },
      detail,
      profile,
    } = useTaskContext();

    usePresentSiteInspectionNavigateAway();

    const site = useMemo(
      () => (siteId != null ? getSiteById(siteId)! : undefined),
      [getSiteById, siteId],
    );

    const manager = useCreation(() => {
      const inspectionManager =
        category === InspectionCategory.Pre
          ? taskManager.preInspectionManager
          : taskManager.inspectionManager;
      return inspectionManager.createCustomIssueInspectionManager({
        flowId,
        inspectionType,
        customIssue,
        site,
      });
    }, [
      category,
      customIssue,
      flowId,
      inspectionType,
      site,
      taskManager.inspectionManager,
      taskManager.preInspectionManager,
    ]);

    const [staged] = useBehaviorSubject(manager.staged$);

    // a fake check item.
    const medias = useMemo<MediaInfo[]>(
      () => staged?.medias?.map(mediaStagedToMediaInfo) ?? [],
      [staged?.medias],
    );

    const faultLikeSubjects = useMemo(
      () =>
        detail.basicInfo.orderDetail?.serviceSubjects?.filter(
          isFaultLikeServiceSubject,
        ),
      [detail.basicInfo.orderDetail?.serviceSubjects],
    );

    const { form, dirty, validateAll } = useFormController<CustomIssueForm>({
      siteName: {
        defaultValue: customIssue?.siteName ?? '',
        validators: [
          s => {
            if (siteId != null || (s != null && s.trim().length > 0)) {
              return true;
            }
            return { error: '请输入部件名称' };
          },
        ],
      },
      inspectionResult: {
        required: true,
        defaultValue: customIssue?.inspectionResult ?? '',
      },
      maintenanceAdvice: {
        required: true,
        defaultValue: customIssue?.maintenanceAdvice ?? '',
      },
      defectiveLevel: {
        required: true,
        defaultValue:
          customIssue?.abnormalLevel != null &&
          customIssue?.severityLevel != null
            ? getDefectiveLevel(
                customIssue.abnormalLevel,
                customIssue.severityLevel,
              )
            : undefined,
      },
      influenceFactor: {
        required: true,
        defaultValue:
          customIssue != null
            ? matchCustomIssueInfluenceFactor(
                customIssue.itemInfluenceFactor,
                customIssue.defectiveClass,
              )
            : undefined,
      },
      description: {
        defaultValue: customIssue != null ? customIssue.description : undefined,
      },
      relatedFaultLikeSubjects: {
        defaultValue: faultLikeSubjects?.map(subject => ({
          name: subject.name,
          checked: subject.relatedInspections?.some(
            x =>
              x.type === 'custom-issue' &&
              ((site != null && x.siteCode === site.code) ||
                (site == null && x.siteName === customIssue?.siteName)) &&
              (x.itemName ?? '') === (customIssue?.itemName ?? ''),
          ),
        })),
      },
    });

    const { bottomComponent$ } = useSiteInspectionNavigatorContext();
    const setBottomComponent = useBehaviorSubjectUpdater(bottomComponent$);

    const willFinish = useRef(false);
    const onDonePress = usePersistFn(async () => {
      const valid = await validateAll();
      if (!valid) {
        if (form.influenceFactor.value == null) {
          void showAlert('请选择故障影响');
          return;
        }
        if (form.defectiveLevel.value == null) {
          void showAlert('请选择处理建议');
        }
        return;
      }
      willFinish.current = true;
      manager.produce(draft => {
        const { abnormalLevel, severityLevel } = defectiveLevelDistributed(
          form.defectiveLevel.value!,
        );
        draft.abnormalLevel = abnormalLevel;
        draft.severityLevel = severityLevel;
        draft.maintenanceAdvice = form.maintenanceAdvice.value;
        draft.inspectionResult = form.inspectionResult.value;
        if (siteId == null) {
          draft.siteInfluenceFactor = form.influenceFactor.value?.value;
        } else {
          draft.itemInfluenceFactor = form.influenceFactor.value?.value;
        }
        draft.defectiveClass = form.influenceFactor.value?.defectiveClass;
        draft.description = form.description.value;
        draft.referenceState = getDefectiveLevelText(
          form.defectiveLevel.value!,
        );
        draft.siteId = siteId;
        draft.siteName = site != null ? site.name : form.siteName.value;
        draft.itemName = '自定义';
        draft.relatedFaultLikeSubjects = form.relatedFaultLikeSubjects.value;
      });
      onDone?.(manager);
    });

    const footer = useMemo(
      () => (
        <BottomAction containerStyle={{ paddingBottom: 8 }}>
          <BottomAction.Button text="完成" onPress={onDonePress} />
        </BottomAction>
      ),
      [onDonePress],
    );

    useEffect(() => {
      delayed(() => {
        setBottomComponent(footer);
      }, 200);
    }, [footer, setBottomComponent]);

    const navigation =
      useNavigation<StackNavigationProp<SiteInspectionNavParams>>();

    useEffect(() => {
      const remove = navigation.addListener('beforeRemove', async e => {
        if ((!dirty && !staged.dirty) || willFinish.current) return;
        e.preventDefault();
        const action = e.data.action;
        const result = await withConfirmation({
          title: '提示',
          message: '返回后您所填写的信息将丢失，是否继续?',
          confirmButtonText: '是',
          cancelButtonText: '否',
        });
        if (result) {
          navigation.dispatch(action);
        }
      });
      return remove;
    }, [dirty, navigation, staged.dirty]);

    const appNavigation = useAppNavigation();

    const watermark = useCallback(
      (scale?: number) => (
        <TaskImageWatermark
          licensePlateNo={detail.basicInfo.licensePlateNo}
          title={staged.siteName ?? staged.itemName ?? '自定义故障'}
          author={profile.user.name}
          store={profile.store.name}
          type="inspection"
          scale={scale}
        />
      ),
      [
        detail.basicInfo.licensePlateNo,
        profile.store.name,
        profile.user.name,
        staged.itemName,
        staged.siteName,
      ],
    );

    const takeMedia = useMediaCaptureWithAnnotation({
      scene: ImageAnnotationScene.Inspection,
    });

    const onTakeMedia = usePersistFn(() => {
      takeMedia({
        tags: () => PredefinedAnnotationTags.inspection,
        defaultTag: () => ({
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          name: staged.inspectionResult || '故障',
          color:
            form.defectiveLevel.value === DefectiveLevel.Urgent
              ? 'red'
              : 'warning',
        }),
        watermark,
        uploadVideoFile: async (localFileUri, contentType, rotate) => {
          taskManager.uploadController.enqueue({
            localFileUri,
            contentType,
            type: 'video',
            parameters: {
              cover: true,
              blurhash: true,
              videoRotate: rotate,
            },
            context: {
              type: 'custom-issue-inspection',
            },
          });
        },
        handleImageAnnotationResults: async results => {
          results.forEach(result => {
            taskManager.uploadController.enqueue({
              localFileUri: result.imageUri,
              contentType: 'image/jpeg',
              type: 'image',
              parameters: {
                cover: true,
                blurhash: true,
              },
              context: {
                type: 'custom-issue-inspection',
                annotationMetadata: result.annotationMetadata,
              },
              listener: (e, task) => {
                if (e.type === 'finished') {
                  // upload the original image.
                  const media = manager.findMedia(task.key);

                  if (!media) return;

                  if (media.annotationMetadata?.origin_uri) return;
                  if (result.originalImageUri != null) {
                    taskManager.largeMediaUploadController.enqueue({
                      localFileUri: result.originalImageUri,
                      contentType: 'image/jpeg',
                      type: 'image',
                      parameters: {
                        blurhash: true,
                      },
                      onSuccess: ({ result: { url } }) => {
                        manager.produce((draft, original) => {
                          const index = original.medias.findIndex(
                            x => x._key === media._key,
                          );
                          const metadata =
                            draft.medias[index]?.annotationMetadata;
                          if (metadata) {
                            metadata.origin_uri = url;
                          }
                        });
                      },
                    });
                  }
                }
              },
            });
          });
        },
      });
    });

    const onDeleteMedia = useCallback(
      (media: MediaInfo) => {
        manager.removeMedia(media.key);
      },
      [manager],
    );

    const onEditMedia = usePersistFn((mediaInfo: MediaInfo) => {
      const mediaStaged = manager.findMedia(mediaInfo.key);
      if (!mediaStaged) return;
      const originalImageUri = mediaStaged.annotationMetadata?.origin_uri;
      appNavigation.navigate('_imageAnnotation', {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        imageUri: originalImageUri || mediaInfo.url,
        tags: PredefinedAnnotationTags.inspection,
        annotationMetadata: originalImageUri
          ? mediaStaged.annotationMetadata
          : undefined,
        watermark,
        onDone: results => {
          if (
            JSON.stringify(mediaStaged.annotationMetadata ?? null) ===
            JSON.stringify(results.annotationMetadata ?? null)
          ) {
            return;
          }
          taskManager.uploadController.enqueue({
            localFileUri: results.imageUri,
            contentType: 'image/jpeg',
            type: 'image',
            // simulateFailure: true,
            parameters: {
              cover: true,
              blurhash: true,
            },
            context: {
              type: 'custom-issue-inspection',
              key: mediaInfo.key,
            },
            listener: e => {
              if (e.type === 'finished') {
                if (mediaStaged.annotationMetadata?.origin_uri) return;

                // use the non-annotated media as the original url.
                if (mediaStaged.annotationMetadata == null) {
                  manager.produce((draft, original) => {
                    const index = original.medias.findIndex(
                      x => x._key === mediaStaged._key,
                    );
                    const metadata = draft.medias[index]?.annotationMetadata;
                    if (metadata) {
                      metadata.origin_uri = mediaInfo.url;
                    }
                  });
                }
              }
            },
          });
          appNavigation.pop();
        },
      });
    });

    const onRetryMediaUpload = useCallback(
      (media: MediaInfo) => {
        if (media.source?.type !== 'local') return;
        taskManager.uploadController.enqueue({
          localFileUri: media.source.localFileUri,
          contentType: media.source.contentType,
          type: media.type,
          parameters: media.source.uploadParameters,
          context: {
            type: 'custom-issue-inspection',
            key: media.key,
          },
        });
      },
      [taskManager.uploadController],
    );

    const onPreviewVideo = useCallback(
      (media: MediaInfo) => {
        const stagedMedia = manager.findMedia(media.key);
        if (
          stagedMedia &&
          stagedMedia._source.type === 'local' &&
          stagedMedia._source.state.status === 'uploaded'
        ) {
          appNavigation.navigate('_mediaPreview', {
            type: 'video',
            url: stagedMedia._source.localFileUri,
          });
        } else {
          appNavigation.navigate('_mediaPreview', {
            type: 'video',
            url: media.url,
          });
        }
      },
      [appNavigation, manager],
    );

    const onDeleteCustomIssue = useCallback(async () => {
      if (
        await withConfirmation({
          title: '确认',
          message: '您即将删除该自定义部位故障，该操作不可撤销，是否继续? ',
        })
      ) {
        onDelete?.(manager);
      }
    }, [manager, onDelete]);

    const onToggleRelatedFaultLikeSubject = useCallback(
      (name: string) => {
        const current = [...(form.relatedFaultLikeSubjects.value ?? [])];
        const index = current.findIndex(x => x.name === name);
        if (index >= 0) {
          current[index] = {
            ...current[index],
            checked: !current[index].checked,
          };
          form.relatedFaultLikeSubjects.update(current);
        }
      },
      [form.relatedFaultLikeSubjects],
    );

    const HeaderRight = useCallback(() => {
      return (
        <LinkButton
          text="删除该故障"
          icon={
            <Icon name="trash-o" size={rpx(16)} color={StatusColors.Danger} />
          }
          textStyle={{
            fontFamily: FontFamily.NotoSans.Light,
            fontSize: rpx(14),
            color: StatusColors.Danger,
          }}
          onPress={onDeleteCustomIssue}
          css={`
            margin-right: ${rpx(15)}px;
            top: ${rpx(-5)}px;
          `}
        />
      );
    }, [onDeleteCustomIssue, rpx]);

    useEffect(() => {
      if (customIssue?.id && !customIssue.siteId) {
        navigation.setOptions({
          headerRight: HeaderRight,
        });
      }
    }, [HeaderRight, customIssue?.id, customIssue?.siteId, navigation]);

    return (
      <ScrollViewContainer
        bottomSpacing={rpx(100)}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        {siteId == null && (
          <Section
            title="部件名称"
            titleStyle={{
              marginLeft: rpx(20),
              marginTop: rpx(10),
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingRight: rpx(10),
            }}
            titleRight={
              <LinkButton
                text="选择..."
                textSize={13}
                onPress={() => {
                  navigation.navigate('_selectCustomIssue', {
                    onSelect: stat => {
                      form.siteName.update(stat.siteName);
                      if (stat.topMaintenanceAdvice) {
                        form.maintenanceAdvice.update(
                          stat.topMaintenanceAdvice,
                        );
                      }
                      if (stat.inspectionResult) {
                        form.inspectionResult.update(stat.inspectionResult);
                      }
                      form.defectiveLevel.update(
                        getDefectiveLevel(
                          stat.abnormalLevel,
                          stat.severityLevel,
                        ),
                      );
                      if (stat.defectiveClass) {
                        const factor = CustomIssueInfluenceFactors.find(
                          f => f.defectiveClass === stat.defectiveClass,
                        );
                        if (factor) {
                          form.influenceFactor.update(factor);
                          form.description.update(factor.description);
                        }
                      }
                    },
                  });
                }}
              />
            }
          >
            <Row>
              <Input
                placeholderTextColor={placeholderColor}
                value={form.siteName.value}
                placeholder="例如: 空气滤芯"
                errorMsg={form.siteName.error}
                style={styles.formInput}
                onChangeText={form.siteName.update}
                onBlur={form.siteName.onBlur}
              />
            </Row>
          </Section>
        )}

        <Section
          title="故障形式"
          titleStyle={{ marginLeft: rpx(10), marginTop: rpx(10) }}
        >
          <Row>
            <Input
              placeholderTextColor={placeholderColor}
              value={form.inspectionResult.value}
              placeholder="例如: 严重老化"
              errorMsg={form.inspectionResult.error}
              style={styles.formInput}
              onChangeText={form.inspectionResult.update}
              onBlur={form.inspectionResult.onBlur}
            />
          </Row>
        </Section>

        <Section
          title="处理方案"
          titleStyle={{ marginLeft: rpx(10), marginTop: rpx(10) }}
        >
          <Row>
            <Input
              placeholderTextColor={placeholderColor}
              value={form.maintenanceAdvice.value}
              placeholder="例如: 更换空气滤芯"
              errorMsg={form.maintenanceAdvice.error}
              style={styles.formInput}
              onChangeText={form.maintenanceAdvice.update}
              onBlur={form.maintenanceAdvice.onBlur}
            />
          </Row>
        </Section>

        <Section
          title="处理建议"
          titleStyle={{ marginLeft: rpx(10), marginTop: rpx(10) }}
          contentStyle={{ borderWidth: 0 }}
        >
          <Row
            cellContainerStyle={{ paddingLeft: 0, paddingRight: 0 }}
            css={`
              padding: 0;
            `}
          >
            <AppHoldItem
              presentOnPress
              component={({ close }) => (
                <AppMenu>
                  {DefectiveLevels.map(level => (
                    <AppMenu.Item
                      key={level}
                      title={getDefectiveLevelText(level)}
                      textStyle={{ fontSize: rpx(15) }}
                      onPress={() =>
                        close(() => form.defectiveLevel.update(level))
                      }
                    />
                  ))}
                </AppMenu>
              )}
            >
              <View
                css={`
                  border-radius: ${rpx(8)}px;
                  background-color: #fff;
                `}
              >
                <HStack
                  css={`
                    background-color: #fff;
                    border-radius: ${rpx(8)}px;
                    overflow: hidden;
                    border-width: 1px;
                    padding: ${rpx(15)}px;
                    border-color: ${form.defectiveLevel.value == null
                      ? borderColor
                      : getDefectiveLevelColor(
                          theme,
                          form.defectiveLevel.value,
                        )};
                    background-color: ${form.defectiveLevel.value == null
                      ? '#fff'
                      : getDefectiveLevelBgColor(
                          theme,
                          form.defectiveLevel.value,
                        )};
                  `}
                >
                  <Typography.Label
                    size={rpx(16)}
                    family={
                      form.defectiveLevel.value == null ? 'light' : 'medium'
                    }
                    color={
                      form.defectiveLevel.value == null
                        ? placeholderColor
                        : getDefectiveLevelColor(
                            theme,
                            form.defectiveLevel.value,
                          )
                    }
                  >
                    {form.defectiveLevel.value == null
                      ? '请选择故障等级'
                      : getDefectiveLevelText(form.defectiveLevel.value)}
                  </Typography.Label>
                  <Icon name="chevron-down" color="#444" size={rpx(24)} />
                </HStack>
              </View>
            </AppHoldItem>
          </Row>
        </Section>

        <Section
          title="故障影响"
          titleStyle={{ marginLeft: rpx(10), marginTop: rpx(10) }}
          contentStyle={{ borderWidth: 0 }}
        >
          <Row
            cellContainerStyle={{ paddingLeft: 0, paddingRight: 0 }}
            css={`
              padding: 0;
            `}
          >
            <AppHoldItem
              presentOnPress
              placement={{
                position: 'below',
                align: 'left',
                anchor: 'left',
                matchWidth: true,
              }}
              component={({ close }) => (
                <AppMenu>
                  {CustomIssueInfluenceFactors.map((item, i) => (
                    <TouchableOpacity
                      key={item.title}
                      css={`
                        padding: ${rpx(10)}px ${rpx(15)}px;
                        border-top-width: 1px;
                        border-top-color: ${i > 0 ? '#ddd' : 'transparent'};
                      `}
                      onPress={() =>
                        close(() => {
                          form.influenceFactor.update(item);
                          form.description.update(item.description);
                        })
                      }
                    >
                      <InfluenceFactorCell value={item} />
                    </TouchableOpacity>
                  ))}
                </AppMenu>
              )}
            >
              <View
                css={`
                  border-radius: ${rpx(8)}px;
                  background-color: #fff;
                  margin: 0;
                `}
              >
                <HStack
                  css={`
                    border-radius: ${rpx(8)}px;
                    overflow: hidden;
                    padding: ${rpx(15)}px;
                    border-width: 1px;
                    border-color: ${form.influenceFactor.value == null
                      ? borderColor
                      : getDefectiveLevelColor(
                          theme,
                          form.influenceFactor.value.defectiveLevel,
                        )};
                    background-color: ${form.influenceFactor.value == null
                      ? '#fff'
                      : getDefectiveLevelBgColor(
                          theme,
                          form.influenceFactor.value.defectiveLevel,
                        )};
                  `}
                >
                  {form.influenceFactor.value == null ? (
                    <Typography.Label
                      size={rpx(16)}
                      light
                      color={placeholderColor}
                    >
                      请选择故障影响
                    </Typography.Label>
                  ) : (
                    <InfluenceFactorCell
                      showDefectiveColor
                      value={form.influenceFactor.value}
                      css={`
                        flex: 1;
                      `}
                    />
                  )}
                  <Icon name="chevron-down" color="#444" size={rpx(24)} />
                </HStack>
              </View>
            </AppHoldItem>
          </Row>
        </Section>

        <RelatedFaultLikeSubjectsToSolve
          title="相关故障检修"
          subjects={form.relatedFaultLikeSubjects.value ?? []}
          onToggle={onToggleRelatedFaultLikeSubject}
          style={{ marginHorizontal: rpx(15), marginTop: rpx(15) }}
          labelStyle={styles.sectionTitleText}
        />

        <Section titleStyle={{ marginLeft: rpx(10), marginTop: rpx(10) }}>
          <Row>
            <Input
              placeholder={'其它故障描述'}
              placeholderTextColor={placeholderColor}
              value={form.description.value ?? ''}
              errorMsg={form.description.error}
              style={styles.formInput}
              onChangeText={form.description.update}
              onBlur={form.description.onBlur}
              multiline
              numberOfLines={0}
              textAlignVertical="top"
              scrollEnabled
              withVoiceInput={isVoiceInputEnabled}
              css={`
                min-height: ${rpx(60)}px;
              `}
            />
          </Row>
        </Section>

        {showMedias && (
          <View
            css={`
              padding: ${rpx(15)}px;
              margin-top: ${rpx(5)}px;
            `}
          >
            <ItemMediaList
              medias={medias}
              editable
              onTakeMedia={onTakeMedia}
              onDeleteMedia={onDeleteMedia}
              onEditMedia={onEditMedia}
              onPreviewVideo={onPreviewVideo}
              onRetryMediaUpload={onRetryMediaUpload}
            />
          </View>
        )}
      </ScrollViewContainer>
    );
  },
  ({ route }) => ({
    title: route.params.title ?? '自定义故障',
    cardStyle: {
      backgroundColor: '#f5f5f5',
    },
  }),
);

const InfluenceFactorCell = memo(
  ({
    value,
    style,
    showDefectiveColor,
    defaultTitleColor,
    defaultDescriptionColor,
  }: {
    value: CustomIssueInfluenceFactor;
    style?: StyleProp<ViewStyle>;
    showDefectiveColor?: boolean;
    defaultTitleColor?: string;
    defaultDescriptionColor?: string;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const defectiveColor = getDefectiveLevelColor(theme, value.defectiveLevel);
    const titleColor = showDefectiveColor
      ? defectiveColor
      : defaultTitleColor ?? '#111';
    const descriptionColor = showDefectiveColor
      ? defectiveColor
      : defaultDescriptionColor ?? '#888';
    return (
      <View style={style}>
        <Typography.Label
          medium
          size={rpx(16)}
          css={`
            color: ${titleColor};
          `}
        >
          {value.title}
        </Typography.Label>
        <Typography.Label
          light
          size={rpx(13)}
          css={`
            color: ${descriptionColor};
          `}
        >
          {value.description}
        </Typography.Label>
      </View>
    );
  },
);

const Row = memo(({ separatorStyle, style, ...props }: ItemProps) => {
  const styles = useStyles();
  return (
    <TableView.Item
      style={[styles.row, style]}
      separatorStyle={[styles.separator, separatorStyle]}
      {...props}
    />
  );
});
Row.displayName = 'TableItem';

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  const theme = useTheme();
  return useMemo(
    () =>
      StyleSheet.create({
        row: {
          paddingTop: rpx(5),
          paddingBottom: rpx(5),
          paddingLeft: 0,
          paddingRight: 0,
        },
        separator: {
          marginLeft: rpx(15),
          backgroundColor: '#f2f2f2',
        },
        section: {
          marginTop: rpx(15),
          marginLeft: rpx(15),
          marginRight: rpx(15),
        },
        sectionTitleText: {
          color: theme.inspection.customIssue.labelColor,
          fontSize: rpx(16),
          lineHeight: rpx(18),
          marginBottom: rpx(5),
          fontFamily: FontFamily.NotoSans.Regular,
        },
        sectionContent: {
          backgroundColor: '#fff',
          borderRadius: rpx(8),
          borderWidth: 1,
          borderColor: '#e7e7e7',
          padding: 0,
        },
        formInput: {
          borderWidth: 0,
          backgroundColor: 'transparent',
          paddingTop: rpx(8),
          paddingBottom: rpx(8),
          paddingLeft: 0,
          paddingRight: 0,
          fontSize: rpx(16),
        },
      }),
    [rpx, theme.inspection.customIssue.labelColor],
  );
};

const Section = memo(
  ({ titleTextStyle, contentStyle, style, ...props }: SectionProps) => {
    const styles = useStyles();
    return (
      <TableView.Section
        style={[styles.section, style]}
        titleTextStyle={[styles.sectionTitleText, titleTextStyle]}
        contentStyle={[styles.sectionContent, contentStyle]}
        {...props}
      />
    );
  },
);
Section.displayName = 'TableSection';
